import type { TargetProbeLegRecord } from '@nadoutong/contracts';
import type { I18nKey, TParams } from '../../i18n/index.ts';
import { legEvidence, manualLegEvidence } from './legEvidence.ts';
import { immunityMinutesRemaining, type LegLamp, type LegSkuArming } from './legModel.ts';
import { legStateSeverity, type LegState, type PhysicalLeg, type RegionSlice } from './legPhysical.ts';
import type { EffectiveRouting, LegRoutingRole } from './legRouting.ts';

type Translate = (key: I18nKey, params?: TParams) => string;

/** Chip text: the long state names LegRow already used. */
export function legStateLabel(state: LegState, immuneLeft: number | null, t: Translate): string {
  switch (state) {
    case 'ok':
      return t('guaranteed.leg.chip.ok');
    case 'shadow':
      return t('guaranteed.leg.chip.shadow');
    case 'failing':
      return t('guaranteed.leg.chip.failing');
    case 'tripped':
      return t('guaranteed.leg.chip.tripped');
    case 'record':
      return t('guaranteed.leg.chip.tripped_unarmed');
    case 'manual':
      return t('guaranteed.leg.chip.manual');
    case 'immune':
      return t('guaranteed.leg.chip.immune', { minutes: String(immuneLeft ?? 0) });
    case 'unprobed':
      return t('guaranteed.leg.unprobed');
  }
}

export function legStateShort(state: LegState, t: Translate): string {
  switch (state) {
    case 'ok':
      return t('guaranteed.legs.short.ok');
    case 'failing':
      return t('guaranteed.legs.short.failing');
    case 'shadow':
      return t('guaranteed.legs.short.shadow');
    case 'tripped':
      return t('guaranteed.legs.short.tripped');
    case 'record':
      return t('guaranteed.legs.short.record');
    case 'immune':
      return t('guaranteed.legs.short.immune');
    case 'unprobed':
      return t('guaranteed.legs.short.unprobed');
    case 'manual':
      return t('guaranteed.legs.short.manual');
  }
}

/** Longest immunity left across the slices, whole minutes; null when none. */
export function legImmuneLeft(leg: PhysicalLeg, nowMs: number): number | null {
  const left = leg.slices
    .map((entry) => immunityMinutesRemaining(entry.slice.immune_until, nowMs))
    .filter((value): value is number => value !== null);
  return left.length === 0 ? null : Math.max(...left);
}

export function routingLabel(routing: EffectiveRouting, globalCount: number, t: Translate): string {
  const name = routing.configName ?? routing.configId ?? '?';
  switch (routing.kind) {
    case 'override':
      return t('guaranteed.legs.routing.override', { name });
    case 'global':
      return globalCount > 1
        ? t('guaranteed.legs.routing.global_named', { name })
        : t('guaranteed.legs.routing.global');
    case 'default':
      return t('guaranteed.legs.routing.default');
    case 'org':
      return t('guaranteed.legs.routing.org', { name, count: String(routing.assignedOrgs ?? 0) });
    case 'unknown':
      return t('guaranteed.legs.routing.unknown');
  }
}

export function routingLabeler(routings: readonly EffectiveRouting[], t: Translate): (routing: EffectiveRouting) => string {
  const globals = routings.filter((routing) => routing.kind === 'global').length;
  return (routing) => routingLabel(routing, globals, t);
}

/** Routing name without the customer count: the share table prints the count on its own line. */
export function routingShortLabel(routing: EffectiveRouting, globalCount: number, t: Translate): string {
  const name = routing.configName ?? routing.configId ?? '?';
  return routing.kind === 'org' ? name : routingLabel(routing, globalCount, t);
}

/** Compact chip text: 「全局默认」, 「frai-专属 · 2 客户」. */
export function routingChipLabeler(routings: readonly EffectiveRouting[], t: Translate): (routing: EffectiveRouting) => string {
  const globals = routings.filter((routing) => routing.kind === 'global').length;
  return (routing) => (routing.kind === 'org'
    ? t('guaranteed.legs.routing.org_chip', {
      name: routing.configName ?? routing.configId ?? '?',
      count: String(routing.assignedOrgs ?? 0),
    })
    : routingLabel(routing, globals, t));
}

/** 「首发 50%」 / 「兜底」 / 「权重未知」, plus 「（已禁用）」 when this routing's targets are off. */
export function roleText(role: LegRoutingRole, t: Translate): string {
  const { share } = role;
  const base = share.role === 'primary'
    ? share.percent === null
      ? t('guaranteed.legs.role.primary_plain')
      : t('guaranteed.legs.role.primary', { percent: String(share.percent) })
    : share.role === 'fallback'
      ? t('guaranteed.legs.role.fallback')
      : t('guaranteed.legs.role.unknown');
  const takeover = takeoverRegionsText(role, t);
  const withTakeover = takeover.length === 0 ? base : `${base} · ${takeover}`;
  return share.disabled ? `${withTakeover}${t('guaranteed.legs.role.disabled')}` : withTakeover;
}

/** 「JP / CN 接管首发」, or '' when this role did not take over anywhere. */
export function takeoverRegionsText(role: LegRoutingRole, t: Translate): string {
  if (role.takeover.length === 0) return '';
  return t('guaranteed.legs.takeover', { regions: role.takeover.map((region) => region.toUpperCase()).join(' / ') });
}

/** Every routing where this leg now takes the first pick: 「全局默认：JP 接管首发」. */
export function takeoverText(placed: { readonly roles: readonly LegRoutingRole[] }, t: Translate): string {
  const globals = placed.roles.filter((role) => role.routing.kind === 'global').length;
  return placed.roles
    .filter((role) => role.takeover.length > 0)
    .map((role) => `${routingLabel(role.routing, globals, t)}：${takeoverRegionsText(role, t)}`)
    .join(' · ');
}

/** 「全局默认 首发 50% · 客户配置 frai-专属（2 个客户）兜底」 */
export function rolesSentence(
  roles: readonly LegRoutingRole[],
  label: (routing: EffectiveRouting) => string,
  t: Translate,
): string {
  return roles.map((role) => {
    const name = label(role.routing);
    // 「（2 个客户）兜底」: no space after a full-width closing bracket.
    return `${name}${name.endsWith('）') ? '' : ' '}${roleText(role, t)}`;
  }).join(' · ');
}

function lampOf(state: LegState): LegLamp {
  return state === 'record' ? 'tripped' : state;
}

export type EvidenceTone = 'bad' | 'warn' | 'plain';

export interface EvidenceLine {
  /** `JP` / `CN` / 两区; empty for a manual leg (the removal covers the whole leg). */
  readonly tag: string;
  readonly text: string;
  /** Hover text; carries the long 「机制已关」 note for a removal record. */
  readonly title: string;
  readonly tone: EvidenceTone;
}

function toneOf(state: LegState): EvidenceTone {
  if (state === 'tripped') return 'bad';
  return state === 'shadow' || state === 'failing' ? 'warn' : 'plain';
}

function sliceText(entry: RegionSlice, nowMs: number, t: Translate, arming: LegSkuArming): { text: string; title: string } {
  // A removal record demotes nothing: no 「→ 时间 摘除」 next to 「机制已关，不影响路由」.
  const raw = legEvidence(entry.slice, lampOf(entry.state), nowMs, t, arming, { trippedAt: entry.state !== 'record' });
  if (entry.state !== 'record') return { text: raw, title: raw };
  return {
    text: `${raw} · ${t('guaranteed.leg.verdict.unarmed_short')}`,
    title: `${raw}\n${t('guaranteed.leg.verdict.unarmed')}`,
  };
}

function newestSlice(leg: PhysicalLeg): TargetProbeLegRecord {
  let newest = leg.head;
  for (const { slice } of leg.slices) {
    if (slice.last_probe_at === null) continue;
    if (newest.last_probe_at === null || Date.parse(slice.last_probe_at) > Date.parse(newest.last_probe_at)) newest = slice;
  }
  return newest;
}

/**
 * 判定与证据, per region in legEvidence wording. Regions that read alike
 * fold into one 两区 line; otherwise the worse region leads.
 */
export function evidenceLines(leg: PhysicalLeg, arming: LegSkuArming, nowMs: number, t: Translate): readonly EvidenceLine[] {
  if (leg.state === 'manual') {
    const text = manualLegEvidence(newestSlice(leg), nowMs, t);
    return [{ tag: '', text, title: text, tone: 'plain' }];
  }
  const first = leg.slices[0];
  if (first === undefined) return [];
  const tagFor = (entry: RegionSlice): string => entry.region.toUpperCase();
  const bothTag = leg.slices.length > 1 ? t('guaranteed.legs.ev.both') : tagFor(first);
  const sameState = leg.slices.every((entry) => entry.state === first.state);
  if (sameState && (first.state === 'ok' || first.state === 'unprobed' || leg.slices.length === 1)) {
    const newest = leg.slices.find((entry) => entry.slice === newestSlice(leg)) ?? first;
    const { text, title } = sliceText(newest, nowMs, t, arming);
    return [{ tag: bothTag, text, title, tone: toneOf(first.state) }];
  }
  const texts = leg.slices.map((entry) => ({ entry, ...sliceText(entry, nowMs, t, arming) }));
  if (texts.every((entry) => entry.text === texts[0]?.text)) {
    const only = texts[0];
    if (only !== undefined) return [{ tag: bothTag, text: only.text, title: only.title, tone: toneOf(first.state) }];
  }
  return [...texts]
    .sort((left, right) => legStateSeverity(right.entry.state) - legStateSeverity(left.entry.state))
    .map((entry) => ({ tag: tagFor(entry.entry), text: entry.text, title: entry.title, tone: toneOf(entry.entry.state) }));
}
